import React, { useCallback } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  Linking,
} from 'react-native';
import { useNavigation } from '@react-navigation/native';
import { StackNavigationProp } from '@react-navigation/stack';
import Icon from 'react-native-vector-icons/Ionicons';
import { useAppSelector } from '@/store';
import { RootStackParamList } from '@/navigation/RootNavigator';
import { RESOURCES_COLORS } from '@/constants/residents/mobile/resourcesColors';
import { MOBILE_TYPOGRAPHY } from '@/constants/residents/mobile/mobileTypography';
import { homeStrings } from '@/constants/residents/strings';
import { formatAsPhoneNumber } from '@/utils/residents/utils';
import { showConfirmWithButtons } from '@/utils/common/alert';

// Layout components
import { BaseResidentMobileScreen } from '@/components/residents/mobile/BaseResidentMobileScreen';
import { MobileScreenLayout } from '@/components/residents/mobile/MobileScreenLayout';
import { BackLink } from '@/components/residents/mobile/BackLink';
import { DetailHeaderCard } from '@/components/residents/mobile/DetailHeaderCard';

type NavigationProp = StackNavigationProp<RootStackParamList>;

/**
 * Mobile Contact Office Screen
 * Matches new Figma design with dark theme
 */
export const MobileContactOfficeScreen: React.FC = () => {
  const navigation = useNavigation<NavigationProp>();
  const { user } = useAppSelector((state) => state.auth);
  const property = user?.property;

  // Handle back navigation
  const handleBack = useCallback(() => {
    navigation.goBack();
  }, [navigation]);

  // Handle website press
  const handleWebsitePress = useCallback((url: string) => {
    navigation.navigate('WebView', { url, title: 'Website' });
  }, [navigation]);

  // Handle phone press
  const handlePhonePress = useCallback((phone: string) => {
    showConfirmWithButtons(
      'Contact',
      'How would you like to contact?',
      'Call',
      'Text',
      () => {
        const phoneUrl = `tel:${phone}`;
        Linking.canOpenURL(phoneUrl).then((supported) => {
          if (supported) {
            Linking.openURL(phoneUrl);
          }
        });
      },
      () => {
        const smsUrl = `sms:${phone}`;
        Linking.canOpenURL(smsUrl).then((supported) => {
          if (supported) {
            Linking.openURL(smsUrl);
          }
        });
      }
    );
  }, []);

  // Handle email press
  const handleEmailPress = useCallback((email: string) => {
    const emailUrl = `mailto:${email}`;
    Linking.canOpenURL(emailUrl).then((supported) => {
      if (supported) {
        Linking.openURL(emailUrl);
      }
    });
  }, []);

  // Handle handbook press
  const handleHandbookPress = useCallback(() => {
    const faqUrl = property?.faqUrl || property?.websites?.handbook;
    if (faqUrl) {
      navigation.navigate('WebView', {
        url: faqUrl,
        title: homeStrings.faqs.title,
      });
    }
  }, [navigation, property]);

  // Format address string
  const formatAddress = (address: any): string | null => {
    if (!address) return null;
    if (typeof address === 'string') {
      return address;
    }
    const street = address.street || address.address1 || address.line1;
    const city = address.city;
    const state = address.state || address.stateCode;
    const zipCode = address.zipCode || address.zip || address.postalCode;
    if (street && city && state && zipCode) {
      return `${street}, ${city}, ${state} ${zipCode}`;
    }
    return null;
  };

  const formattedAddress = property ? formatAddress(property.address) : null;
  const hasHandbook = property?.websites?.handbook || property?.faqUrl;

  return (
    <BaseResidentMobileScreen
      screenName="Resources"
      darkBackground={true}
    >
      <MobileScreenLayout
        backgroundColor={RESOURCES_COLORS.background}
        lightStatusBar={true}
      >
        {/* Back Navigation */}
        <BackLink
          label="Back to resources"
          onPress={handleBack}
          color={RESOURCES_COLORS.linkBlue}
        />

        {/* Header Card with Property Photo */}
        <DetailHeaderCard
          title="Contact the office"
          description={property?.marketingName || 'Get in touch with your property management'}
          imageUri={property?.photoUrl}
          icon={<Icon name="chatbubble-outline" size={24} color={RESOURCES_COLORS.textPrimary} />}
          cardBackground={RESOURCES_COLORS.cardBackground}
          accentColor={RESOURCES_COLORS.linkPink}
          titleColor={RESOURCES_COLORS.textPrimary}
          descriptionColor={RESOURCES_COLORS.textPrimary}
        />

        {/* Website */}
        {property?.websites?.home && (
          <TouchableOpacity
            style={styles.infoCard}
            onPress={() => handleWebsitePress(property.websites!.home!)}
          >
            <View style={styles.infoIconContainer}>
              <Icon name="globe-outline" size={20} color={RESOURCES_COLORS.textPrimary} />
            </View>
            <View style={styles.infoContent}>
              <Text style={styles.infoLabel}>WEBSITE</Text>
              <Text style={styles.infoValue}>{property.websites.home}</Text>
            </View>
            <Icon name="chevron-forward" size={20} color={RESOURCES_COLORS.textSecondary} />
          </TouchableOpacity>
        )}

        {/* Phone */}
        {property?.phone && (
          <TouchableOpacity
            style={styles.infoCard}
            onPress={() => handlePhonePress(property.phone!)}
          >
            <View style={styles.infoIconContainer}>
              <Icon name="call-outline" size={20} color={RESOURCES_COLORS.textPrimary} />
            </View>
            <View style={styles.infoContent}>
              <Text style={styles.infoLabel}>PHONE</Text>
              <Text style={styles.infoValue}>{formatAsPhoneNumber(property.phone)}</Text>
            </View>
            <Icon name="chevron-forward" size={20} color={RESOURCES_COLORS.textSecondary} />
          </TouchableOpacity>
        )}

        {/* Email */}
        {property?.email && (
          <TouchableOpacity
            style={styles.infoCard}
            onPress={() => handleEmailPress(property.email!)}
          >
            <View style={styles.infoIconContainer}>
              <Icon name="mail-outline" size={20} color={RESOURCES_COLORS.textPrimary} />
            </View>
            <View style={styles.infoContent}>
              <Text style={styles.infoLabel}>EMAIL</Text>
              <Text style={styles.infoValue}>{property.email}</Text>
            </View>
            <Icon name="chevron-forward" size={20} color={RESOURCES_COLORS.textSecondary} />
          </TouchableOpacity>
        )}

        {/* Address */}
        {formattedAddress && (
          <View style={styles.infoCard}>
            <View style={styles.infoIconContainer}>
              <Icon name="location-outline" size={20} color={RESOURCES_COLORS.textPrimary} />
            </View>
            <View style={styles.infoContent}>
              <Text style={styles.infoLabel}>ADDRESS</Text>
              <Text style={styles.infoValue}>{formattedAddress}</Text>
            </View>
          </View>
        )}

        {/* Resident Handbook */}
        {hasHandbook && (
          <TouchableOpacity
            style={styles.infoCard}
            onPress={handleHandbookPress}
          >
            <View style={styles.infoIconContainer}>
              <Icon name="book-outline" size={20} color={RESOURCES_COLORS.textPrimary} />
            </View>
            <View style={styles.infoContent}>
              <Text style={styles.infoLabel}>RESIDENT HANDBOOK</Text>
              <Text style={styles.infoValue}>View FAQs and guidelines</Text>
            </View>
            <Icon name="chevron-forward" size={20} color={RESOURCES_COLORS.textSecondary} />
          </TouchableOpacity>
        )}
      </MobileScreenLayout>
    </BaseResidentMobileScreen>
  );
};

const styles = StyleSheet.create({
  infoCard: {
    backgroundColor: RESOURCES_COLORS.cardBackground,
    borderRadius: 16,
    padding: 16,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
  },
  infoIconContainer: {
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: RESOURCES_COLORS.avatarBackground,
    justifyContent: 'center',
    alignItems: 'center',
  },
  infoContent: {
    flex: 1,
    gap: 2,
  },
  infoLabel: {
    ...MOBILE_TYPOGRAPHY.label,
    color: RESOURCES_COLORS.textMuted,
  },
  infoValue: {
    ...MOBILE_TYPOGRAPHY.body,
    color: RESOURCES_COLORS.textPrimary,
  },
});
